import { Link, useParams } from 'react-router-dom';
import { Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { useProducer } from '@/hooks/useProducer';
import { usePublicProductById } from '@/hooks/usePublicProductById';
import { stripHtmlToText } from '@/lib/text';
import { producerProductPath } from '@/lib/paths';

function formatPrice(ore: number, currency: string = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(ore / 100);
}

export default function ProductEmbedPage() {
  const { id } = useParams<{ id: string }>();
  const { product, loading: productLoading } = usePublicProductById(id);
  const { producer, loading: producerLoading } = useProducer(product?.producer_id || '');
  const { data: coverImages = {} } = useProductCoverImages(product ? [product.id] : [], 640);

  const isLoading = productLoading || (product ? producerLoading : false);

  if (isLoading) {
    return (
      <main className="min-h-screen bg-background/40 p-3 sm:p-4">
        <Skeleton className="h-[320px] rounded-xl" />
      </main>
    );
  }

  if (!product || !producer || producer.status !== 'public') {
    return (
      <main className="min-h-screen bg-background/40 p-4">
        <section className="rounded-xl border border-border/40 bg-background/80 p-6 text-center">
          <p className="text-sm text-muted-foreground">Produkten hittades inte.</p>
        </section>
      </main>
    );
  }

  const coverUrl = coverImages[product.id] ?? null;
  const productPath = producerProductPath(producer.slug || producer.id, product.slug || product.id);
  const description = stripHtmlToText(product.description || '');

  return (
    <main className="min-h-screen bg-background/40 p-3 sm:p-4">
      <section className="mx-auto max-w-xl rounded-xl border border-border/40 bg-background shadow-sm overflow-hidden">
        {coverUrl ? (
          <div className="aspect-[16/10] bg-muted">
            <img src={coverUrl} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-muted flex items-center justify-center">
            <Store className="h-10 w-10 text-muted-foreground/40" />
          </div>
        )}

        <div className="space-y-3 p-4 sm:p-5">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Från {producer.name}</p>
            <h1 className="text-lg font-semibold text-foreground leading-snug">{product.title}</h1>
          </div>

          <p className="text-xl font-semibold text-foreground">
            {formatPrice(product.price_ore, product.currency)}
          </p>

          {description ? (
            <p className="text-sm text-muted-foreground line-clamp-3">{description}</p>
          ) : null}

          <Button asChild className="w-full sm:w-auto">
            <Link to={productPath} target="_blank" rel="noopener noreferrer">
              Visa produkt
            </Link>
          </Button>
        </div>
      </section>
    </main>
  );
}
